// product-variant.js
(function () {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  // Ensure utils is loaded before proceeding
  if (typeof window.requireUtils !== 'function') {
    console.error(
      '[product-variant.js] window.requireUtils is not defined. utils.js may not have loaded.'
    );
    return;
  }

  try {
    window.requireUtils('product-variant.js');
  } catch (e) {
    console.error('[product-variant.js] Dependency check failed:', e.message);
    return; // Fail silently if utils not available (prevents further errors)
  }

  const state = {
    basePriceCents: 0,
    addonsCents: 0,
    quantity: 1,
    currency: null,
    locale: null,
  };

  function initPriceState() {
    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;

    const centsAttr = priceEl.getAttribute('data-product-price-cents');
    state.basePriceCents = centsAttr ? parseInt(centsAttr, 10) || 0 : 0;

    state.currency =
      priceEl.getAttribute('data-currency') ||
      document.querySelector('[data-product-add-to-cart-price]')?.getAttribute('data-currency') ||
      'EUR';

    state.locale =
      priceEl.getAttribute('data-locale') ||
      document.querySelector('[data-product-add-to-cart-price]')?.getAttribute('data-locale') ||
      undefined;
  }

  function formatMoneyFromCents(cents) {
    return window.utils.formatMoney(cents, {
      locale: state.locale,
      currency: state.currency || 'EUR',
    });
  }

  function updateAddToCartPrice() {
    const els = document.querySelectorAll('[data-product-add-to-cart-price]');
    if (!els.length) return;

    // Check if subscription is selected (prices will be handled by subscription script)
    const subscriptionSection = document.querySelector('[data-ck-subscription]');
    if (subscriptionSection) {
      const checkedOption = subscriptionSection.querySelector(
        '[data-sub-option-card][aria-checked="true"]'
      );
      if (checkedOption && checkedOption.dataset.subOption === 'subscription') {
        // Subscription prices are handled by subscription script, skip here
        return;
      }
    }

    const quantity = Math.max(1, state.quantity || 1);
    const totalCents = (state.basePriceCents + state.addonsCents) * quantity;
    const formatted = formatMoneyFromCents(totalCents);

    els.forEach((el) => {
      el.textContent = formatted;
    });
  }

  // Listen for subscription price update requests
  window.addEventListener('subscription:price-update-requested', () => {
    updateAddToCartPrice();
  });

  // Listen for subscription discounted price updates
  window.addEventListener('subscription:discounted-price-updated', (event) => {
    const { discountedPriceCents } = event.detail || {};
    if (discountedPriceCents !== undefined) {
      // Temporarily override base price for add-to-cart calculation
      const originalBasePrice = state.basePriceCents;
      state.basePriceCents = discountedPriceCents;
      updateAddToCartPrice();
      // Restore original base price (subscription script handles display)
      state.basePriceCents = originalBasePrice;
    }
  });

  function getQuantityFromInput(quantityInput) {
    if (!quantityInput) return 1;

    // Try internal input first
    const innerInput = quantityInput.querySelector('input');
    if (innerInput && innerInput.value != null) {
      const n = parseFloat(innerInput.value);
      return isNaN(n) ? 1 : n;
    }

    // Fallback: attribute or property on the host element
    const raw = (quantityInput.value ?? quantityInput.getAttribute('value') ?? '1') + '';
    const n = parseFloat(raw);
    return isNaN(n) ? 1 : n;
  }

  // Store observer reference to allow cleanup
  let quantityObserver = null;

  function wireQuantityListeners() {
    const quantityInput = document.querySelector('ck-quantity-input');
    if (!quantityInput) return;

    // Disconnect any existing observer before creating a new one
    if (quantityObserver) {
      quantityObserver.disconnect();
      quantityObserver = null;
    }

    const recalcFromQuantity = () => {
      state.quantity = getQuantityFromInput(quantityInput);
      updateAddToCartPrice();
    };

    // Listen on the host
    quantityInput.addEventListener('input', recalcFromQuantity);
    quantityInput.addEventListener('change', recalcFromQuantity);
    quantityInput.addEventListener('ck-change', recalcFromQuantity);

    // And on the internal <input>, if it exists
    const inner = quantityInput.querySelector('input');
    if (inner) {
      inner.addEventListener('input', recalcFromQuantity);
      inner.addEventListener('change', recalcFromQuantity);
    }

    // Fallback: observe the "value" attribute
    quantityObserver = new MutationObserver((records) => {
      for (const rec of records) {
        if (rec.type === 'attributes' && rec.attributeName === 'value') {
          recalcFromQuantity();
        }
      }
    });

    quantityObserver.observe(quantityInput, {
      attributes: true,
      attributeFilter: ['value'],
    });

    // Initial sync
    recalcFromQuantity();
  }

  function recomputeAddonsFromDOM() {
    let total = 0;
    const checkboxes = document.querySelectorAll('ck-checkbox[data-addon-price-cents]');

    checkboxes.forEach((cb) => {
      const isChecked =
        cb.checked || cb.hasAttribute('checked') || cb.getAttribute('aria-checked') === 'true';

      if (isChecked) {
        const centsAttr = cb.getAttribute('data-addon-price-cents');
        const cents = centsAttr ? parseInt(centsAttr, 10) || 0 : 0;
        total += cents;
      }
    });

    state.addonsCents = total;
    updateAddToCartPrice();
  }

  function wireAddonListeners() {
    const checkboxes = document.querySelectorAll('ck-checkbox[data-addon-price-cents]');
    if (!checkboxes.length) return;

    const handler = () => {
      // Instead of trusting the event target, just rescan them all
      recomputeAddonsFromDOM();
    };

    checkboxes.forEach((cb) => {
      cb.addEventListener('click', handler);
      cb.addEventListener('change', handler);
      cb.addEventListener('ck-change', handler);
    });

    // Initial state
    recomputeAddonsFromDOM();
  }

  function selectVariantForProduct(productId, variantId) {
    if (!productId || !variantId) return;

    const selectEl = document.querySelector(`[data-product-variant-select="${productId}"]`);
    if (!selectEl) return;

    const normalizedId = String(variantId);
    const optionTag = selectEl.querySelector(`option[value="${normalizedId}"]`);
    if (!optionTag) return;

    if (selectEl.value !== normalizedId) {
      selectEl.value = normalizedId;
    }
    selectEl.dispatchEvent(new Event('change', { bubbles: true }));

    // Get price from the option
    const priceCentsAttr = optionTag.getAttribute('data-price-cents');
    const priceCents = priceCentsAttr ? parseInt(priceCentsAttr, 10) || 0 : 0;

    // Update state
    state.basePriceCents = priceCents;

    // Clear stored original price when variant changes (subscription script will recalculate)
    const priceEl = document.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.removeAttribute('data-original-price-cents');
    }

    // Update unit price display
    const priceEls = document.querySelectorAll('[data-product-price]');
    const unitFormatted = formatMoneyFromCents(priceCents);
    priceEls.forEach((el) => {
      el.textContent = unitFormatted;
      el.setAttribute('data-product-price-cents', String(priceCents));
    });

    // Update add-to-cart button price
    updateAddToCartPrice();

    // Update stock quantity display
    updateStockQuantity(optionTag);

    // Update URL with variant parameter
    updateURLWithVariant(variantId);

    // Reorder product images to show variant image first
    reorderProductImages(productId, variantId);
  }

  function updateStockQuantity(optionElement) {
    if (!optionElement) return;

    // Check if inventory is tracked
    const inventoryManagement = optionElement.getAttribute('data-inventory-management');
    const isTracked = inventoryManagement !== null && inventoryManagement !== '';

    // If inventory is not tracked, hide stock quantity display and remove inventory restrictions
    if (!isTracked) {
      // Hide stock quantity text display - look for paragraphs containing "left in stock"
      document.querySelectorAll('p.text-sm.text-foreground-secondary').forEach((pEl) => {
        const text = (pEl.textContent || '').trim();
        if (text.includes('left in stock')) {
          pEl.style.display = 'none';
        }
      });

      // Remove inventory restrictions from quantity input
      const quantityInput = document.querySelector('ck-quantity-input');
      if (quantityInput) {
        quantityInput.removeAttribute('max');
        quantityInput.removeAttribute('data-inventory-quantity');
        const innerInput = quantityInput.querySelector('input');
        if (innerInput) {
          innerInput.removeAttribute('max');
          innerInput.removeAttribute('data-inventory-quantity');
        }
      }
      return;
    }

    // Inventory is tracked - proceed with normal stock quantity update
    const inventoryQuantity = optionElement.getAttribute('data-inventory-quantity');
    const inventoryQty = window.utils?.normalizeInventoryQuantity
      ? window.utils.normalizeInventoryQuantity(inventoryQuantity)
      : Math.max(0, inventoryQuantity ? parseInt(inventoryQuantity, 10) : 0);

    // Update stock quantity text display - look for paragraphs containing "left in stock"
    // Only show if quantity is 5 or less (including 0 for out of stock)
    document.querySelectorAll('p.text-sm.text-foreground-secondary').forEach((pEl) => {
      const text = (pEl.textContent || '').trim();
      if (text.includes('left in stock')) {
        if (inventoryQty <= 5) {
          pEl.style.display = ''; // Show the element if it was hidden
          const semiboldSpan = pEl.querySelector('span.font-semibold');
          if (semiboldSpan) {
            semiboldSpan.textContent = inventoryQty;
          }
        } else {
          // Hide if quantity is greater than 5
          pEl.style.display = 'none';
        }
      }
    });

    // Update quantity input max attribute
    const quantityInput = document.querySelector('ck-quantity-input');
    if (quantityInput) {
      quantityInput.setAttribute('max', String(inventoryQty));
      const innerInput = quantityInput.querySelector('input');
      if (innerInput) {
        innerInput.setAttribute('max', String(inventoryQty));
        innerInput.setAttribute('data-inventory-quantity', String(inventoryQty));
      }
      quantityInput.setAttribute('data-inventory-quantity', String(inventoryQty));
    }
  }

  function updateURLWithVariant(variantId) {
    if (!variantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));

    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());
  }

  const variantImagesCache = new Map();

  function getVariantImageMapping(productId) {
    if (!productId) return null;
    if (variantImagesCache.has(productId)) return variantImagesCache.get(productId);

    const jsonEl = document.getElementById(`ProductVariantImagesJson-${productId}`);
    if (!jsonEl) {
      variantImagesCache.set(productId, null);
      return null;
    }

    try {
      const parsed = JSON.parse(jsonEl.textContent || '{}');
      variantImagesCache.set(productId, parsed);
      return parsed;
    } catch {
      variantImagesCache.set(productId, null);
      return null;
    }
  }

  function getVariantImageId(productId, variantId) {
    if (!productId || !variantId) return null;
    const mapping = getVariantImageMapping(productId);
    if (!mapping) return null;

    const variantIdStr = String(variantId);
    const imageId = mapping[variantIdStr];
    if (imageId && imageId !== 'null') {
      return String(imageId);
    }

    return null;
  }

  function updateDesktopImageOrder(container, targetImageId) {
    if (!container || !targetImageId) return;
    const desktopItems = Array.from(container.querySelectorAll('[data-product-image-item]'));
    if (!desktopItems.length) return;

    let targetItem = null;
    desktopItems.forEach((item, index) => {
      const defaultOrder = Number(item.dataset.defaultOrder ?? index);
      item.style.setProperty('order', String(defaultOrder));
      if (item.getAttribute('data-image-id') === targetImageId) {
        targetItem = item;
      }
    });

    if (!targetItem) {
      targetItem = desktopItems[0];
    }

    if (targetItem) {
      targetItem.style.setProperty('order', '-1');
    }
  }

  function setActiveCarouselSlide(mediaContainer, imageId) {
    if (!mediaContainer || !imageId) return;
    const carousel = mediaContainer.querySelector('ck-carousel');
    if (!carousel) return;

    const slides = Array.from(carousel.querySelectorAll('ck-carousel-item'));
    if (!slides.length) return;

    const targetIndex = slides.findIndex((item) => item.getAttribute('data-image-id') === imageId);
    const activeIndex = targetIndex >= 0 ? targetIndex : 0;

    carousel.setAttribute('active-index', String(activeIndex));
    if ('activeIndex' in carousel) {
      carousel.activeIndex = activeIndex;
    }
  }

  function reorderProductImages(productId, variantId) {
    if (!productId || !variantId) return;

    const mediaContainer = document.querySelector(
      `[data-product-media-container][data-product-id="${productId}"]`
    );
    if (!mediaContainer) {
      console.warn(`[product-variant] Media container not found for product ${productId}`);
      return;
    }

    const imageItems = Array.from(mediaContainer.querySelectorAll('[data-product-image-item]'));
    if (!imageItems.length) {
      console.warn(`[product-variant] No image items found in container`);
      return;
    }

    const availableImageIds = imageItems
      .map((item) => item.getAttribute('data-image-id'))
      .filter(Boolean);

    let resolvedImageId = getVariantImageId(productId, variantId);
    if (resolvedImageId && !availableImageIds.includes(resolvedImageId)) {
      console.warn(
        `[product-variant] Image with ID ${resolvedImageId} not found for variant ${variantId}. Available IDs: ${availableImageIds.join(
          ', '
        )}`
      );
      resolvedImageId = null;
    }

    if (!resolvedImageId) {
      resolvedImageId = availableImageIds[0] || null;
    }

    if (!resolvedImageId) {
      console.warn(`[product-variant] No target image ID determined for variant ${variantId}`);
      return;
    }

    const desktopContainer = mediaContainer.querySelector('[data-product-media-desktop]');
    updateDesktopImageOrder(desktopContainer, resolvedImageId);
    setActiveCarouselSlide(mediaContainer, resolvedImageId);
  }

  function initializeProductImageOrder(productId) {
    if (!productId) return;

    const selectEl = document.querySelector(`[data-product-variant-select="${productId}"]`);
    if (!selectEl || !selectEl.value) return;

    // Reorder images for the initially selected variant
    reorderProductImages(productId, selectEl.value);
  }

  const variantsCache = new Map();

  function getVariantsForProduct(productId) {
    if (!productId) return [];
    if (variantsCache.has(productId)) return variantsCache.get(productId);

    const jsonEl = document.getElementById(`ProductVariantsJson-${productId}`);
    if (!jsonEl) {
      variantsCache.set(productId, []);
      return [];
    }

    try {
      const parsed = JSON.parse(jsonEl.textContent || '[]');
      const arr = Array.isArray(parsed) ? parsed : [];
      variantsCache.set(productId, arr);
      return arr;
    } catch {
      variantsCache.set(productId, []);
      return [];
    }
  }

  function getSelectionsFromCurrentSelect(selectEl, variants) {
    const selectedId = selectEl && selectEl.value ? String(selectEl.value) : null;
    if (!selectedId) return [null, null, null];

    const found = variants.find((v) => String(v.id) === selectedId);
    if (!found) return [null, null, null];

    return [found.option1 || null, found.option2 || null, found.option3 || null];
  }

  function findBestMatchingVariant(variants, selections) {
    if (!variants || !variants.length) return null;

    const matches = (v) => {
      if (!v) return false;
      if (selections[0] && v.option1 !== selections[0]) return false;
      if (selections[1] && v.option2 !== selections[1]) return false;
      if (selections[2] && v.option3 !== selections[2]) return false;
      return true;
    };

    // Prefer available variants first
    const available = variants.find((v) => matches(v) && v.available);
    if (available) return available;

    return variants.find((v) => matches(v)) || null;
  }

  function wireColorPickers() {
    const pickers = document.querySelectorAll('ck-color-picker[data-product-id]');

    pickers.forEach((picker) => {
      const productId = picker.dataset.productId;
      if (!productId) return;

      const selectEl = document.querySelector(`[data-product-variant-select="${productId}"]`);
      if (!selectEl) return;

      const variants = getVariantsForProduct(productId);
      const optionIndexRaw = picker.dataset.colorOptionIndex;
      const optionIndex = optionIndexRaw != null ? parseInt(optionIndexRaw, 10) : null;

      picker.addEventListener('ck-change', (event) => {
        const selectedOptionValue = event.detail && event.detail.value;
        if (!selectedOptionValue) return;

        const selections = getSelectionsFromCurrentSelect(selectEl, variants);
        if (optionIndex === 0 || optionIndex === 1 || optionIndex === 2) {
          selections[optionIndex] = selectedOptionValue;
        }

        const matched = findBestMatchingVariant(variants, selections);
        if (!matched || !matched.id) return;

        selectVariantForProduct(productId, matched.id);
      });
    });
  }

  function wireRadioCardVariantPickers() {
    const groups = document.querySelectorAll(
      '[data-variant-picker][data-product-id][data-option-index], [data-package-size-picker][data-product-id][data-option-index]'
    );

    groups.forEach((group) => {
      const productId = group.getAttribute('data-product-id');
      if (!productId) return;

      const selectEl = document.querySelector(`[data-product-variant-select="${productId}"]`);
      if (!selectEl) return;

      const variants = getVariantsForProduct(productId);
      const optionIndexRaw = group.getAttribute('data-option-index');
      const optionIndex = optionIndexRaw != null ? parseInt(optionIndexRaw, 10) : null;

      if (!(optionIndex === 0 || optionIndex === 1 || optionIndex === 2)) return;

      const cards = group.querySelectorAll('ck-radio-card[data-option-value]');
      if (!cards.length) return;

      const syncFromSelect = () => {
        const selections = getSelectionsFromCurrentSelect(selectEl, variants);
        const currentValue = selections[optionIndex];
        cards.forEach((card) => {
          const cardValue = card.getAttribute('data-option-value');
          if (!cardValue) return;
          const shouldCheck = cardValue === currentValue;
          if (card.checked !== shouldCheck) {
            card.checked = shouldCheck;
          }
        });
      };

      cards.forEach((card) => {
        card.addEventListener('ck-change', (event) => {
          if (!event.detail || !event.detail.checked) return;
          const value = card.getAttribute('data-option-value');
          if (!value) return;

          const selections = getSelectionsFromCurrentSelect(selectEl, variants);
          selections[optionIndex] = value;

          const matched = findBestMatchingVariant(variants, selections);
          if (!matched || !matched.id) return;

          selectVariantForProduct(productId, matched.id);
        });
      });

      selectEl.addEventListener('change', syncFromSelect);
      syncFromSelect();
    });
  }

  function wireVariantSelectListeners() {
    const variantSelects = document.querySelectorAll('[data-product-variant-select]');
    variantSelects.forEach((selectEl) => {
      const productId = selectEl.getAttribute('data-product-variant-select');
      if (!productId) return;

      selectEl.addEventListener('change', (event) => {
        const variantId = event.target.value;
        if (variantId) {
          // Update stock quantity when variant changes
          const selectedOption = selectEl.options[selectEl.selectedIndex];
          if (selectedOption) {
            updateStockQuantity(selectedOption);
          }

          reorderProductImages(productId, variantId);
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initPriceState();
    wireColorPickers();
    wireRadioCardVariantPickers();
    wireQuantityListeners();
    wireAddonListeners();
    wireVariantSelectListeners();

    // Initialize image order for all products on page
    const productContainers = document.querySelectorAll('[data-product-media-container]');
    productContainers.forEach((container) => {
      const productId = container.getAttribute('data-product-id');
      if (productId) {
        initializeProductImageOrder(productId);
      }
    });

    // Initial total
    updateAddToCartPrice();

    // Initialize stock quantity for selected variant on page load
    const variantSelects = document.querySelectorAll('[data-product-variant-select]');
    variantSelects.forEach((selectEl) => {
      if (selectEl.value && selectEl.options[selectEl.selectedIndex]) {
        updateStockQuantity(selectEl.options[selectEl.selectedIndex]);
      }
    });
  });

  if (typeof window !== 'undefined') {
    window.selectVariantForProduct = selectVariantForProduct;
  }
})();
